import SiteNavBar from '../../components/feature/SiteNavBar';
import SiteFooter from '../../components/feature/SiteFooter';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { useTranslation } from 'react-i18next';

export default function LagePage() {
  const { t } = useTranslation();
  const { ref: refHead, isVisible: visHead } = useScrollReveal();
  const { ref: refNeigh, isVisible: visNeigh } = useScrollReveal();
  const { ref: refDist, isVisible: visDist } = useScrollReveal();

  const neighborhoods = [
    { title: t('location.page.privacy'), text: t('location.page.privacyText'), icon: 'ri-shield-line' },
    { title: t('location.page.education'), text: t('location.page.educationText'), icon: 'ri-graduation-cap-line' },
    { title: t('location.page.nature'), text: t('location.page.natureText'), icon: 'ri-plant-line' },
    { title: t('location.page.culture'), text: t('location.page.cultureText'), icon: 'ri-restaurant-line' },
  ];

  const distances = [
    { icon: 'ri-goblet-line', label: 'Dolder Grand Hotel', distance: '4 min', note: t('location.page.walking', 'Zu Fuß') },
    { icon: 'ri-store-line', label: 'Bahnhofstrasse', distance: '12 min', note: t('location.page.byCar', 'Mit dem Auto') },
    { icon: 'ri-flight-takeoff-line', label: 'Flughafen Zürich', distance: '25 min', note: t('location.page.byCar', 'Mit dem Auto') },
    { icon: 'ri-school-line', label: 'International School', distance: '8 min', note: t('location.page.byCar', 'Mit dem Auto') },
    { icon: 'ri-hospital-line', label: 'Zürich Stadtspital', distance: '10 min', note: t('location.page.byCar', 'Mit dem Auto') },
    { icon: 'ri-train-line', label: 'Zürich HB', distance: '15 min', note: t('location.page.byCar', 'Mit dem Auto') },
    { icon: 'ri-golf-ball-line', label: 'Golf Club Zürich', distance: '6 min', note: t('location.page.byCar', 'Mit dem Auto') },
    { icon: 'ri-sailboat-line', label: 'Zürichsee Bootshafen', distance: '9 min', note: t('location.page.byCar', 'Mit dem Auto') },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <SiteNavBar />
      <section className="relative h-72 flex items-end pb-14 overflow-hidden">
        <img src="https://public.readdy.ai/ai/img_res/79120408016120be0296212c96df5bb8.jpg" alt="Lage Hero" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
        <div className="relative z-10 max-w-7xl mx-auto px-8 w-full">
          <p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A] mb-3">The Obsidian Villa</p>
          <h1 className="font-serif text-4xl md:text-5xl font-medium text-white">{t('location.page.title')}</h1>
        </div>
      </section>

      <section className="py-20 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-8">
          <div ref={refHead} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center reveal-up ${visHead ? 'visible' : ''}`}>
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="gold-line" />
                <p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A]">Zürichberg, Zürich</p>
              </div>
              <h2 className="font-serif text-4xl font-medium text-[#1C1C1C] mb-8 leading-snug">{t('location.page.heading')}</h2>
              <p className="font-sans text-[15px] text-[#6B6460] leading-loose font-light mb-6">{t('location.page.text1')}</p>
              <p className="font-sans text-[15px] text-[#6B6460] leading-loose font-light">{t('location.page.text2')}</p>
            </div>
            <div className="relative">
              <img src="https://readdy.ai/api/search-image?query=The%20Obsidian%20Villa%20north%20garden%20entrance%20view%2C%20pale%20limestone%20facade%20with%20dark%20anodized%20aluminum%20frames%2C%20private%20gravel%20driveway%20through%20forest%2C%20Zurichberg%20tall%20oak%20trees%2C%20Lake%20Zurich%20glimmering%20below%20in%20valley%2C%20warm%20late%20afternoon%20light%2C%20architectural%20photography&width=700&height=520&seq=9002&orientation=landscape" alt="The Obsidian Villa Einfahrt" className="w-full h-80 object-cover" />
              <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b border-r border-[#B59A6A] pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F5F3EF] py-20">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-center gap-4 mb-10">
            <div className="gold-line" />
            <p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A]">{t('location.page.mapLabel')}</p>
          </div>
          <div className="relative w-full h-[520px] border border-[#E5DDD3] overflow-hidden">
            <iframe title="Zürichberg Karte" width="100%" height="100%" style={{ border: 0, filter: 'saturate(0.25) contrast(1.15) brightness(1.05)' }} loading="lazy" allowFullScreen
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5418.2!2d8.5735!3d47.3758!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479aa39994b6db55%3A0x8d93f5e7e4c74aa!2sZ%C3%BCrichberg%2C%20Zurich!5e0!3m2!1sde!2sch!4v1234567890" />
            <div className="absolute bottom-6 left-6 bg-white/95 px-5 py-3 border border-[#E5DDD3]">
              <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#1C1C1C]">{t('location.page.mapAddress')}</p>
              <p className="font-sans text-[10px] text-[#B59A6A] mt-0.5">{t('location.page.mapNote')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-8">
          <div ref={refNeigh} className={`reveal-up ${visNeigh ? 'visible' : ''}`}>
            <div className="flex items-center gap-4 mb-12">
              <div className="gold-line" />
              <p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A]">{t('location.page.neighborhood')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {neighborhoods.map((n) => (
                <div key={n.title} className="border border-[#E5DDD3] p-8 bg-white hover:border-[#B59A6A]/40 transition-colors duration-300">
                  <div className="w-10 h-10 flex items-center justify-center mb-5"><i className={`${n.icon} text-xl text-[#B59A6A]`} /></div>
                  <h3 className="font-serif text-lg font-medium text-[#1C1C1C] mb-3">{n.title}</h3>
                  <p className="font-sans text-[13px] text-[#6B6460] leading-relaxed font-light">{n.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F5F3EF]">
        <div className="max-w-7xl mx-auto px-8">
          <div ref={refDist} className={`reveal-up ${visDist ? 'visible' : ''}`}>
            <div className="flex items-center gap-4 mb-12">
              <div className="gold-line" />
              <p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A]">{t('location.page.distances')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-[#E5DDD3]">
              {distances.map((d) => (
                <div key={d.label} className="flex items-center justify-between py-5 px-0 border-b border-[#E5DDD3] pr-8">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 flex items-center justify-center"><i className={`${d.icon} text-[#B59A6A] text-base`} /></div>
                    <div>
                      <p className="font-sans text-sm text-[#1C1C1C] font-light">{d.label}</p>
                      <p className="font-sans text-[10px] text-[#BFBAB5] mt-0.5">{d.note}</p>
                    </div>
                  </div>
                  <span className="font-sans text-[11px] tracking-widest uppercase text-[#B59A6A] font-medium">{d.distance}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
